// **Ein kleiner Router** über die History-API — eine Seite, eine Tabelle
// (routes.ts), ein `<Link>`. Kein react-router: Die Plattform hat zwölf feste
// Pfade und braucht weder verschachtelte Routen noch Lader.
//
// Der Ort (Pfad + Suche) ist React-Zustand; `navigate` schreibt die History
// und setzt ihn, `popstate` (Zurück/Vor) setzt ihn ebenso.

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from "react";
import { isModifiedClick, normalizePath } from "./routes";

export interface Location {
  /** Ohne abschließenden Schrägstrich. */
  pathname: string;
  /** Mit führendem `?`, oder leer. */
  search: string;
}

export type Navigate = (to: string, options?: { replace?: boolean }) => void;

interface RouterValue {
  location: Location;
  navigate: Navigate;
}

const RouterContext = createContext<RouterValue | null>(null);

function readLocation(): Location {
  return { pathname: normalizePath(window.location.pathname), search: window.location.search };
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState<Location>(readLocation);

  useEffect(() => {
    const onPop = () => setLocation(readLocation());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback<Navigate>((to, options) => {
    if (options?.replace) window.history.replaceState({}, "", to);
    else window.history.pushState({}, "", to);
    setLocation(readLocation());
  }, []);

  const value = useMemo(() => ({ location, navigate }), [location, navigate]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

function useRouter(): RouterValue {
  const v = useContext(RouterContext);
  if (!v) throw new Error("RouterProvider fehlt");
  return v;
}

export const useLocation = (): Location => useRouter().location;
export const useNavigate = (): Navigate => useRouter().navigate;

/** Die Suchparameter des Orts (neu je Aufruf — nicht verändern, sondern `setSearch`). */
export function useSearchParams(): URLSearchParams {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

/**
 * Suchparameter ändern, ohne einen Eintrag in der History (`replaceState`) —
 * Filter und aufgeklappte Zeilen sind Zustand der Seite, kein Ort, zu dem
 * „Zurück" führen soll. `null`/leer entfernt einen Parameter; wer einen leeren
 * Wert bewusst stehen lassen will, gibt ihn in `keepEmpty` an.
 */
export function useSetSearch(): (changes: Record<string, string | null>, options?: { keepEmpty?: string[] }) => void {
  const { location, navigate } = useRouter();
  return useCallback((changes, options) => {
    const params = new URLSearchParams(location.search);
    for (const [k, v] of Object.entries(changes)) {
      if (v === null || (v === "" && !options?.keepEmpty?.includes(k))) params.delete(k);
      else params.set(k, v);
    }
    const qs = params.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
  }, [location, navigate]);
}

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & { to: string; replace?: boolean };

/**
 * Ein Verweis innerhalb der Seite: Ein schlichter Linksklick bleibt in der
 * Seite, alles andere (Strg/Cmd/Umschalt, Mittelklick, `target`) behandelt
 * der Browser wie bei jedem Link.
 */
export function Link({ to, replace, onClick, children, ...rest }: LinkProps) {
  const navigate = useNavigate();
  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || isModifiedClick(e) || rest.target) return;
    e.preventDefault();
    navigate(to, { replace });
  };
  return <a href={to} onClick={handle} {...rest}>{children}</a>;
}
